/**
 * WS-GAPS2 B-W11 — route gate for the read-only schedule-conflicts page.
 *
 * LEDGER W4d-3: approvers AND operational dispatchers are notified of reserved-window schedule conflicts; D-1b: that
 * grants a dispatcher no approval authority. The audience is decided by the SERVER predicate
 * `public.is_schedule_conflict_viewer(user, shop)` (approvers via is_manager_or_writer, plus an active DISPATCHER by
 * legacy role or work role) — the same predicate that already gates `list_reserved_window_conflicts`, the
 * `lane_integrity_overlaps` read policy and the dispatcher notification policy. This gate asks it, never decides
 * locally, and fails CLOSED: while pending it shows a loading state, and an unknown answer is a refusal.
 *
 * Presentation only: the page's data is independently refused by the server (42501) to anyone outside the audience.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

type LooseRpc = { rpc: (f: string, a: Record<string, unknown>) => PromiseLike<{ data: unknown; error: unknown }> };

export async function fetchIsScheduleConflictViewer(userId: string, shopId: string): Promise<boolean> {
  try {
    const { data, error } = await (supabase as unknown as LooseRpc).rpc('is_schedule_conflict_viewer', { p_user: userId, p_shop: shopId });
    return !error && data === true;
  } catch {
    return false;
  }
}

export function ScheduleConflictViewerRoute({ children }: { children: ReactNode }) {
  const { user, activeShop } = useAuth();
  const userId = user?.id ?? null;
  const shopId = activeShop?.id ?? null;
  const [state, setState] = useState<'loading' | 'allowed' | 'refused'>('loading');

  useEffect(() => {
    let cancelled = false;
    if (!userId || !shopId) { setState('refused'); return; }
    setState('loading');
    void fetchIsScheduleConflictViewer(userId, shopId).then((ok) => { if (!cancelled) setState(ok ? 'allowed' : 'refused'); });
    return () => { cancelled = true; };
  }, [userId, shopId]);

  if (state === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-xl font-semibold">Loading...</div>
      </div>
    );
  }
  if (state === 'refused') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="mb-4 text-4xl font-bold">403 - Forbidden</h1>
          <p className="text-xl text-muted-foreground">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}
